export type HelpTopicIcon =
  | 'getting-started'
  | 'customers'
  | 'sales-orders'
  | 'purchase-orders'
  | 'invoices'
  | 'reports'
  | 'settings';

export type HelpTopic = {
  id: string;
  title: string;
  description: string;
  icon: HelpTopicIcon;
  articleIds: string[];
};

export type HelpArticle = {
  id: string;
  title: string;
  summary: string;
  topicId: string;
  body: string[];
  /** Public path to a screenshot shown in the article body and related cards. */
  screenshot?: string;
  screenshotAlt?: string;
};

export type HelpFaq = {
  id: string;
  question: string;
  answer: string;
  relatedArticleId?: string;
};

export const helpTopics: HelpTopic[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Learn how Orderflow is organized and set up your team.',
    icon: 'getting-started',
    articleIds: ['getting-started'],
  },
  {
    id: 'customers-vendors',
    title: 'Customers & Vendors',
    description: 'Add and manage the companies you sell to and buy from.',
    icon: 'customers',
    articleIds: ['manage-customers'],
  },
  {
    id: 'sales-orders',
    title: 'Sales Orders',
    description: 'Create sales orders and track them through fulfillment.',
    icon: 'sales-orders',
    articleIds: ['create-sales-order'],
  },
  {
    id: 'purchase-orders',
    title: 'Purchase Orders',
    description: 'Order stock from vendors and receive it into inventory.',
    icon: 'purchase-orders',
    articleIds: ['create-purchase-order'],
  },
  {
    id: 'invoices-payments',
    title: 'Invoices & Payments',
    description: 'Bill customers and record the payments you receive.',
    icon: 'invoices',
    articleIds: ['create-invoice', 'record-payment'],
  },
  {
    id: 'reports',
    title: 'Reports',
    description: 'Understand your business with built-in reporting.',
    icon: 'reports',
    articleIds: ['run-reports'],
  },
  {
    id: 'account-settings',
    title: 'Account & Settings',
    description: 'Configure terms, shipping methods, and system numbers.',
    icon: 'settings',
    articleIds: ['account-settings'],
  },
];

export const helpArticles: HelpArticle[] = [
  {
    id: 'getting-started',
    title: 'Getting started with Orderflow',
    summary:
      'A quick tour of the workspace and the order-to-cash flow that ties everything together.',
    topicId: 'getting-started',
    screenshot: '/help/getting-started.png',
    screenshotAlt: 'Orderflow dashboard with assistant and left navigation',
    body: [
      'Orderflow helps small businesses manage the full order-to-cash cycle: keep a record of your customers and vendors, take sales orders, purchase stock, ship goods, and get paid.',
      'The icons in the left navigation bar are your main entry points. Each icon opens a menu where you can either add a new record or browse existing ones. For example, the Customers icon lets you add a customer or open the customers table.',
      'Most records are created through a pop-up form. Fill in the required fields, save, and the new record appears in the matching browse table. From a table you can search, edit, and open records to see their history.',
      'A typical flow looks like this: add a customer, create a sales order for that customer, generate a packing slip when goods ship, turn the order into an invoice, and finally record the payment when it arrives.',
    ],
  },
  {
    id: 'manage-customers',
    title: 'Managing customers and vendors',
    summary:
      'Add the companies you sell to and buy from, and keep their details up to date.',
    topicId: 'customers-vendors',
    screenshot: '/help/manage-customers.png',
    screenshotAlt: 'Browse Customers table in Orderflow',
    body: [
      'Customers are the companies you sell to; vendors are the companies you buy from. Both are managed the same way from the left navigation bar.',
      'To add a customer, open the Customers menu and choose Add Customer. Enter the company name, contact details, and billing information, then save. The new customer is immediately available when you create sales orders and invoices.',
      'To review or update existing records, choose Browse Customers (or Browse Vendors). Use the search box to filter the table, then open a record to edit its details or view related activity.',
      'Keeping contact and payment terms accurate here means less typing later, because these details are pulled into new orders, invoices, and reports automatically.',
    ],
  },
  {
    id: 'create-sales-order',
    title: 'Creating a sales order',
    summary:
      'Capture what a customer wants to buy and track it through to fulfillment.',
    topicId: 'sales-orders',
    screenshot: '/help/create-sales-order.png',
    screenshotAlt: 'Browse Sales Orders table in Orderflow',
    body: [
      'A sales order records what a customer has agreed to buy. Open the Sales Orders menu in the left navigation and choose Add Sales Order.',
      'Select the customer, then add line items for each product or service. As you add items, Orderflow calculates line totals, tax, and the order total for you.',
      'Save the order to store it. You can reopen it later from Browse Sales Orders to make changes, generate a packing slip when the goods ship, or convert it into an invoice.',
      'Sales orders connect to the rest of the system: packing slips document what shipped, and invoices bill the customer for the order.',
    ],
  },
  {
    id: 'create-purchase-order',
    title: 'Creating a purchase order',
    summary: 'Order stock from a vendor and receive it into your inventory.',
    topicId: 'purchase-orders',
    screenshot: '/help/create-purchase-order.png',
    screenshotAlt: 'Browse Purchase Orders table in Orderflow',
    body: [
      'A purchase order (PO) is a request to buy goods from one of your vendors. Open the Purchase Orders menu and choose Add Purchase Order.',
      'Pick the vendor and add the items you want to order along with quantities and costs. Orderflow totals the PO so you know the expected spend.',
      'Save the PO to send or print it. When the goods arrive you can update the order, and the items are reflected in your inventory so they are available to sell.',
      'Use Browse Purchase Orders to keep an eye on what has been ordered and what is still outstanding.',
    ],
  },
  {
    id: 'create-invoice',
    title: 'Creating an invoice',
    summary: 'Bill a customer for goods or services and track what they owe.',
    topicId: 'invoices-payments',
    screenshot: '/help/create-invoice.png',
    screenshotAlt: 'Browse Invoices table in Orderflow',
    body: [
      'An invoice bills a customer for what they have purchased. You can create one from scratch through the Invoices menu, or generate it from an existing sales order.',
      'Choose the customer and confirm the line items, quantities, and prices. Orderflow applies the customer’s payment terms and calculates tax and totals.',
      'Save the invoice to finalize it. It then appears in Browse Invoices and in your accounts receivable, so you always know what is owed.',
      'When the customer pays, record the payment against the invoice so its balance updates.',
    ],
  },
  {
    id: 'record-payment',
    title: 'Recording a payment',
    summary: 'Apply money received from a customer to their open invoices.',
    topicId: 'invoices-payments',
    screenshot: '/help/record-payment.png',
    screenshotAlt: 'Payment Received screen in Orderflow',
    body: [
      'When a customer pays, record it so their balance stays accurate. Open Finance in the left navigation and choose Payment Received.',
      'Find the customer and the invoice being paid. You can search by company or invoice number to narrow the list.',
      'Enter the amount received and any reference details, then record the payment. The invoice balance is reduced by the amount you applied, and the payment shows in the customer’s history.',
      'Payments feed directly into your accounts receivable and statement reports, so recording them promptly keeps those reports trustworthy.',
    ],
  },
  {
    id: 'run-reports',
    title: 'Running reports',
    summary: 'Use built-in reports to understand sales, tax, and balances.',
    topicId: 'reports',
    screenshot: '/help/run-reports.png',
    screenshotAlt: 'Reports overview with trend cards in Orderflow',
    body: [
      'Reports summarize the activity captured across Orderflow. Open the Reports menu in the left navigation to see the available reports.',
      'The Overview gives you trends at a glance. More detailed reports—such as Sales Tax, Customer Statement, Accounts Receivable, and Accounts Payable—let you choose a date range and filters before generating results.',
      'Most reports let you generate on screen and then download a copy for sharing or record keeping. Adjust the filters and generate again to explore different periods.',
      'Because reports read directly from your orders, invoices, and payments, the more consistently you record those, the more accurate your reports will be.',
    ],
  },
  {
    id: 'account-settings',
    title: 'Account and settings',
    summary:
      'Configure payment terms, shipping methods, and numbering to fit your business.',
    topicId: 'account-settings',
    screenshot: '/help/account-settings.png',
    screenshotAlt: 'Utilities menu showing Settings and related options',
    body: [
      'The Utilities menu in the left navigation is where you tailor Orderflow to your business.',
      'Settings covers general preferences. Payment Terms and Vendor Terms let you define the terms that are applied to new customer and vendor transactions.',
      'Shipping Methods lets you maintain the carriers and methods you use on packing slips, and System Numbers controls how records like orders and invoices are numbered.',
      'Setting these up once means new records are created with sensible defaults, saving time and keeping your documents consistent.',
    ],
  },
];

export const helpFaqs: HelpFaq[] = [
  {
    id: 'faq-create-sales-order',
    question: 'How do I create a sales order?',
    answer:
      'Open the Sales Orders menu in the left navigation, choose Add Sales Order, select the customer, add line items, and save.',
    relatedArticleId: 'create-sales-order',
  },
  {
    id: 'faq-record-payment',
    question: 'Where do I record a payment?',
    answer:
      'Go to Finance → Payment Received, find the customer and invoice, enter the amount, and record it against the invoice.',
    relatedArticleId: 'record-payment',
  },
  {
    id: 'faq-packing-slips',
    question: 'How do packing slips relate to orders?',
    answer:
      'A packing slip documents what shipped for a sales order. You generate it from an order when goods leave, while the invoice bills the customer for the same order.',
    relatedArticleId: 'create-sales-order',
  },
  {
    id: 'faq-invoice-from-order',
    question: 'Can I create an invoice from a sales order?',
    answer:
      'Yes. Open the sales order and convert it into an invoice, or create an invoice directly from the Invoices menu and select the same customer and items.',
    relatedArticleId: 'create-invoice',
  },
  {
    id: 'faq-ar-report',
    question: 'How do I run an accounts receivable report?',
    answer:
      'Open the Reports menu, choose Accounts Receivable Report, pick a date range and any filters, then generate. You can download the results afterward.',
    relatedArticleId: 'run-reports',
  },
  {
    id: 'faq-add-customer',
    question: 'How do I add a new customer?',
    answer:
      'Open the Customers menu, choose Add Customer, fill in the company and contact details, and save. The customer is then available across orders and invoices.',
    relatedArticleId: 'manage-customers',
  },
  {
    id: 'faq-payment-terms',
    question: 'Where do I set payment terms?',
    answer:
      'Payment terms are managed under Utilities → Payment Terms (and Vendor Terms for vendors). They are applied automatically to new transactions.',
    relatedArticleId: 'account-settings',
  },
  {
    id: 'faq-purchase-order',
    question: 'How do I order stock from a vendor?',
    answer:
      'Create a purchase order from the Purchase Orders menu, select the vendor, add items with quantities and costs, and save to send or print it.',
    relatedArticleId: 'create-purchase-order',
  },
];

export function getArticleById(articleId: string): HelpArticle | undefined {
  return helpArticles.find((article) => article.id === articleId);
}

export function getTopicById(topicId: string): HelpTopic | undefined {
  return helpTopics.find((topic) => topic.id === topicId);
}

export function getArticlesByTopic(topicId: string): HelpArticle[] {
  return helpArticles.filter((article) => article.topicId === topicId);
}
